import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Calendar, CurrencyDollar, Clock, ArrowRight, TrendUp, Lightning, Repeat, Warning, Bell, Wallet, Camera, VideoCamera, ChatCircleText } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useCurrency } from "../../lib/useCurrency";
import NotificationToggle from "../../components/NotificationToggle";
import WeeklyDigestCard from "../../components/WeeklyDigestCard";

export default function TrainerDashboard() {
  const { user } = useAuth();
  const { symbol } = useCurrency();
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [billingRuns, setBillingRuns] = useState([]);
  const [billingAttention, setBillingAttention] = useState({ payment_issues: [], upcoming_renewals: [] });
  const [recentProgress, setRecentProgress] = useState([]);

  const load = async () => {
    try {
      const [s, sess, b, br, ba, rp] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/sessions", { params: { from_date: new Date().toISOString() } }),
        api.get("/booking-requests"),
        api.get("/billing/runs"),
        api.get("/dashboard/billing-attention"),
        api.get("/dashboard/recent-progress"),
      ]);
      setStats(s.data);
      setSessions(sess.data.slice(0, 6));
      setBookings(b.data.filter((x) => x.status === "new").slice(0, 5));
      setBillingRuns(br.data.slice(0, 4));
      setBillingAttention(ba.data || { payment_issues: [], upcoming_renewals: [] });
      setRecentProgress(rp.data?.entries || []);
    } catch (_e) {}
  };

  useEffect(() => { load(); }, []);

  const today = new Date();
  const todayLabel = today.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  const [payingNow, setPayingNow] = useState(false);
  const payAutoNow = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (payingNow) return;
    setPayingNow(true);
    try {
      const { data } = await api.post("/payroll/auto-pay/run-now?force=true");
      if (data.settled > 0) {
        toast.success(`Paid ${data.settled} staff payout${data.settled === 1 ? "" : "s"}`);
      } else {
        toast.info("Nothing to pay — staff already paid today or no eligible sessions");
      }
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPayingNow(false);
    }
  };

  const nudge = async (id, name) => {
    try {
      await api.post(`/dashboard/low-pot-clients/${id}/notify`);
      toast.success(`Nudged ${name}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-10" data-testid="trainer-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="tracker text-brand mb-3">[ Command Center ]</div>
          <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl leading-[0.9]" data-testid="dashboard-date-heading">{todayLabel}.</h1>
        </div>
        <Link to="/trainer/schedule" className="btn-primary" data-testid="dashboard-schedule-cta">
          <Lightning size={16} weight="fill" /> Schedule session
        </Link>
      </div>

      <NotificationToggle />

      <WeeklyDigestCard />

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 border border-white/10">
        <StatCard label="Active Clients" value={stats?.total_clients ?? "—"} icon={<Users size={20} weight="duotone" />} />
        <StatCard label="Upcoming Sessions" value={stats?.upcoming_sessions ?? "—"} icon={<Calendar size={20} weight="duotone" />} />
        <StatCard label="Outstanding" value={`${symbol}${(stats?.unpaid_total ?? 0).toLocaleString()}`} icon={<Clock size={20} weight="duotone" />} accent money />
        <StatCard label="Revenue (MTD)" value={`${symbol}${(stats?.revenue_this_month ?? 0).toLocaleString()}`} icon={<TrendUp size={20} weight="duotone" />} money />
      </div>

      {/* Billing preview — upcoming 1st-of-month auto-bill */}
      {stats?.billing_next && stats.billing_next.client_count > 0 && (
        <div className="card-flat px-6 py-4 flex items-center justify-between gap-4 flex-wrap" data-testid="billing-preview">
          <div className="flex items-center gap-3">
            <Repeat size={20} weight="duotone" className="text-brand-light" />
            <div>
              <div className="tracker">Next auto-billing</div>
              <div className="text-xs text-zinc-400 mt-0.5">
                {new Date(stats.billing_next.date_iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
                {" · "}
                {stats.billing_next.client_count} active plan{stats.billing_next.client_count === 1 ? "" : "s"}
              </div>
            </div>
          </div>
          <div className="font-display text-3xl money" data-testid="billing-preview-amount">
            {symbol}{stats.billing_next.total.toLocaleString()}
          </div>
        </div>
      )}

      {/* Auto-pay preview — next staff payout */}
      {stats?.next_auto_pay && stats.next_auto_pay.date_iso && stats.next_auto_pay.staff_count > 0 && (
        <Link to="/trainer/payroll" className="card-flat px-6 py-4 flex items-center justify-between gap-4 flex-wrap hover:border-brand-light/40 transition-colors" data-testid="auto-pay-preview">
          <div className="flex items-center gap-3">
            <Wallet size={20} weight="duotone" className="text-brand-light" />
            <div>
              <div className="tracker">Next auto-pay</div>
              <div className="text-xs text-zinc-400 mt-0.5">
                {new Date(stats.next_auto_pay.date_iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                {" · "}
                {stats.next_auto_pay.staff_count} staff
                {" · "}
                {stats.next_auto_pay.session_count} session{stats.next_auto_pay.session_count === 1 ? "" : "s"} ready
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="font-display text-3xl money" data-testid="auto-pay-preview-amount">
              {symbol}{stats.next_auto_pay.total.toLocaleString()}
            </div>
            <button
              onClick={payAutoNow}
              disabled={payingNow || stats.next_auto_pay.session_count === 0}
              className="btn-primary !py-2 !px-4 !text-xs disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="auto-pay-now-btn"
              title={stats.next_auto_pay.session_count === 0 ? "No eligible sessions to settle right now" : "Pay all eligible auto-pay staff immediately (bypasses weekday wait)"}
            >
              <Lightning size={12} weight="fill" /> {payingNow ? "Paying…" : "Pay now"}
            </button>
          </div>
        </Link>
      )}

      {/* Billing attention — Payment issues + Upcoming renewals */}
      {(billingAttention.payment_issues.length > 0 || billingAttention.upcoming_renewals.length > 0) && (
        <div className="grid lg:grid-cols-2 gap-6" data-testid="billing-attention-widget">
          {billingAttention.payment_issues.length > 0 && (
            <div className="card-flat border-[#FF3B30]/40" data-testid="payment-issues-card">
              <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <Warning size={22} weight="duotone" className="text-[#FF6B61]" />
                  <div>
                    <div className="font-display text-2xl leading-none">Auto-bill failed</div>
                    <div className="tracker mt-1">
                      {billingAttention.payment_issues.length} unresolved invoice{billingAttention.payment_issues.length === 1 ? "" : "s"} —
                      mark paid once you&apos;ve handled them
                    </div>
                  </div>
                </div>
                <span className="tracker px-2 py-1 bg-[#FF3B30] text-white">{billingAttention.payment_issues.length}</span>
              </div>
              <div>
                {billingAttention.payment_issues.slice(0, 5).map((p) => (
                  <div key={p.client_id} className="px-6 py-4 border-b border-white/5 last:border-b-0 grid grid-cols-12 gap-3 items-center" data-testid={`payment-issue-${p.client_id}`}>
                    <div className="col-span-12 sm:col-span-5">
                      <div className="font-semibold">{p.client_name}</div>
                      <div className="font-mono text-xs text-zinc-500">{p.billing_period}</div>
                    </div>
                    <div className="col-span-6 sm:col-span-3">
                      <div className="tracker mb-1">Unpaid</div>
                      <div className="font-mono text-[#FF6B61] font-semibold money">{symbol}{Number(p.amount).toFixed(0)}</div>
                    </div>
                    <div className="col-span-6 sm:col-span-4 text-right">
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Mark ${symbol}${Number(p.amount).toFixed(0)} invoice for ${p.client_name} as paid? This won't charge their card — only use after you've collected payment another way.`)) return;
                          try {
                            await api.patch(`/invoices/${p.invoice_id}/mark-paid`);
                            const ba = await api.get("/dashboard/billing-attention");
                            setBillingAttention(ba.data);
                          } catch (err) {
                            alert("Failed: " + (err.response?.data?.detail || err.message));
                          }
                        }}
                        className="btn-ghost !py-1.5 !px-3 !text-[10px] hover:!border-emerald-400/40 hover:!text-emerald-300"
                        data-testid={`mark-paid-${p.invoice_id}`}
                        title="Mark this invoice as paid (won't charge the card)"
                      >
                        Mark paid
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {billingAttention.upcoming_renewals.length > 0 && (
            <div className="card-flat border-brand/40" data-testid="upcoming-renewals-card">
              <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <Bell size={22} weight="duotone" className="text-brand-light" />
                  <div>
                    <div className="font-display text-2xl leading-none">Renewals coming up</div>
                    <div className="tracker mt-1">{billingAttention.upcoming_renewals.length} client{billingAttention.upcoming_renewals.length === 1 ? "" : "s"} billing in {billingAttention.days_until_next_bill} day{billingAttention.days_until_next_bill === 1 ? "" : "s"}</div>
                  </div>
                </div>
                <span className="tracker px-2 py-1 bg-brand text-white">{billingAttention.upcoming_renewals.length}</span>
              </div>
              <div>
                {billingAttention.upcoming_renewals.slice(0, 5).map((r) => (
                  <div key={r.client_id} className="px-6 py-4 border-b border-white/5 last:border-b-0 grid grid-cols-12 gap-3 items-center" data-testid={`upcoming-renewal-${r.client_id}`}>
                    <div className="col-span-12 sm:col-span-6">
                      <div className="font-semibold">{r.client_name}</div>
                      <div className="font-mono text-xs text-zinc-500">{r.client_email}</div>
                    </div>
                    <div className="col-span-6 sm:col-span-3">
                      <div className="tracker mb-1">Renews</div>
                      <div className="font-mono money text-brand-light font-semibold">{symbol}{Number(r.amount).toFixed(0)}</div>
                    </div>
                    <div className="col-span-6 sm:col-span-3 text-right">
                      <span className="tracker text-[10px] text-zinc-500">{new Date(r.next_bill_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Upcoming sessions */}
        <div className="lg:col-span-7 card-flat" data-testid="upcoming-sessions-widget">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <div className="font-display text-2xl">Upcoming sessions</div>
            <Link to="/trainer/schedule" className="tracker text-brand hover:underline flex items-center gap-1">
              All <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
          {sessions.length === 0 ? (
            <div className="p-10 text-center text-zinc-500">
              <Calendar size={36} weight="duotone" className="mx-auto mb-3 text-zinc-700" />
              <div className="tracker">No sessions scheduled</div>
              <Link to="/trainer/schedule" className="text-brand tracker hover:underline mt-3 inline-block">
                Schedule the first one →
              </Link>
            </div>
          ) : (
            <div>
              {sessions.map((s) => (
                <SessionRow key={s.id} s={s} />
              ))}
            </div>
          )}
        </div>

        {/* New booking requests */}
        <div className="lg:col-span-5 card-flat" data-testid="booking-requests-widget">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <div className="font-display text-2xl">New requests</div>
            <span className="tracker px-2 py-1 bg-brand text-white">{bookings.length}</span>
          </div>
          {bookings.length === 0 ? (
            <div className="p-10 text-center text-zinc-500 tracker">All caught up</div>
          ) : (
            <div>
              {bookings.map((b) => (
                <div key={b.id} className="px-6 py-4 border-b border-white/5 last:border-b-0">
                  <div className="flex items-start justify-between mb-1">
                    <div className="font-semibold">{b.name}</div>
                    <span className="tracker text-brand">{b.preferred_date?.slice(0, 10)}</span>
                  </div>
                  <div className="text-xs text-zinc-500 font-mono">{b.email}{b.phone ? ` · ${b.phone}` : ""}</div>
                  {b.notes && <div className="text-sm text-zinc-400 mt-2 line-clamp-2">{b.notes}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent progress uploads */}
      {recentProgress.length > 0 && (
        <div className="card-flat" data-testid="recent-progress-widget">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <Camera size={20} weight="duotone" className="text-brand-light" />
              <div>
                <div className="font-display text-2xl leading-none">Recent client uploads</div>
                <div className="tracker mt-1">Latest progress entries · review and leave coaching notes</div>
              </div>
            </div>
            <Link to="/trainer/progress" className="tracker text-brand hover:underline flex items-center gap-1">
              All <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5">
            {recentProgress.map((p) => (
              <Link
                key={p.id}
                to="/trainer/progress"
                className="p-5 border-r border-b border-white/10 hover:bg-[#141414] transition-colors"
                data-testid={`recent-progress-tile-${p.id}`}
              >
                <div className="tracker text-[10px] text-zinc-500 mb-2">{p.week_label}</div>
                <div className="font-semibold text-sm truncate">{p.client_name}</div>
                <div className="text-[10px] text-zinc-500 mt-1">{new Date(p.created_at).toLocaleDateString()}</div>
                <div className="flex items-center gap-3 text-zinc-400 text-xs mt-3">
                  <span className="inline-flex items-center gap-1"><Camera size={11} weight="bold" /> {p.photo_count}</span>
                  {p.has_video && <span className="inline-flex items-center gap-1"><VideoCamera size={11} weight="bold" /> 1</span>}
                  {p.comment_count > 0 && <span className="inline-flex items-center gap-1 text-brand-light"><ChatCircleText size={11} weight="bold" /> {p.comment_count}</span>}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Billing runs */}
      <div className="card-flat" data-testid="billing-runs-widget">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Repeat size={18} weight="duotone" className="text-brand" />
            <div>
              <div className="font-display text-2xl leading-none">Monthly auto-billing</div>
              <div className="tracker mt-1">Runs on the 1st of every month · clients with active plans only</div>
            </div>
          </div>
          <Link to="/trainer/clients" className="tracker text-brand hover:underline">Manage plans →</Link>
        </div>
        {billingRuns.length === 0 ? (
          <div className="p-10 text-center text-zinc-500 tracker">No billing runs yet — set up monthly plans on Clients</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4">
            {billingRuns.map((r) => (
              <div key={r.id} className="p-6 border-r border-b border-white/10">
                <div className="tracker mb-3">Period {r.period}</div>
                <div className="font-display text-3xl mb-1">{r.created_count}</div>
                <div className="tracker">invoices created</div>
                <div className="tracker text-zinc-500 mt-3 text-[10px]">{r.skipped_count} skipped · {new Date(r.ran_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, accent, money }) {
  return (
    <div className={`p-6 border-r border-b border-white/10 ${accent ? "bg-[#0E1424]" : ""}`}>
      <div className="flex items-center justify-between mb-6">
        <div className="tracker">{label}</div>
        <div className="text-brand">{icon}</div>
      </div>
      <div className={`font-display text-4xl leading-none ${money ? "money" : ""}`}>{value}</div>
    </div>
  );
}

function SessionRow({ s }) {
  const dt = new Date(s.start_time);
  const day = dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  const time = dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return (
    <div className="px-6 py-4 border-b border-white/5 last:border-b-0 hover:bg-[#1A1A1A] flex items-center gap-4">
      <div className="w-16 text-center">
        <div className="font-display text-2xl text-brand leading-none">{dt.getDate()}</div>
        <div className="tracker mt-1">{dt.toLocaleDateString(undefined, { month: "short" })}</div>
      </div>
      <div className="flex-1">
        <div className="font-semibold">{s.client?.name || "Client"}</div>
        <div className="text-sm text-zinc-400">{s.session_type}</div>
      </div>
      <div className="text-right">
        <div className="font-mono text-sm">{time}</div>
        <div className="tracker">{day} · {s.duration_minutes}m</div>
      </div>
    </div>
  );
}
